import { configure, setDemoProvider } from "@playlive/fundraiser-data/config";
import type { TiltifyCampaign, TiltifyPoll, TiltifyUser } from "@playlive/tiltify-core";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import "./App.css";
import "./index.scss";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  type AdobeDataLayer,
  initAdobeAnalytics,
  normalizeEnvironment,
  type TrackingObject,
} from "@playlive/adobe-analytics";
import { fakeTiltifyCampaign, fakeTiltifyPoll, fakeTiltifyUser } from "@/data/example/index";
import { configurePlayLiveAuth } from "@/util/auth-config";
import { isTestMode } from "@/util/const";

declare global {
  interface OneTrustType {
    ToggleInfoDisplay: () => void;
  }

  interface Window {
    OneTrust: OneTrustType;
    stjude: TrackingObject;
    adobeDataLayer: AdobeDataLayer;
  }
}

initAdobeAnalytics({
  siteCode: "playlive",
  environment: normalizeEnvironment(import.meta.env.VITE_ENV ?? "dev"),
  language: "english",
  defaultPageName: "home",
});

// Wire @playlive/fundraiser-auth's singleton config (Tiltify + Twitch OAuth
// popups, JWKS verification, token refresh). Must run before any auth helper.
configurePlayLiveAuth();

// Wire @playlive/fundraiser-data's singleton config. Every hook in
// @playlive/react-query reads the proxy URL from here — must run
// before any hook renders.
configure({
  tiltifyProxyUrl: import.meta.env.VITE_TILTIFY_PROXY_URL,
  leaderboardApiUrl: import.meta.env.VITE_LEADERBOARD_SERVICE_URI,
  donationTrainApiUrl: import.meta.env.VITE_DONATION_TRAIN_SERVICE_URI,
  lifetimeApiUrl: import.meta.env.VITE_LIFETIME_RAISED_SERVICE_URI,
});

// QA-mode fixture provider. The Tiltify fetchers in
// `@playlive/fundraiser-data` short-circuit to these when a slug or ID
// matches the DEMO_* constants (see `defaultTiltifyConfig`). Restores
// the pre-migration `if (isTestMode) return [fakeTiltifyCampaign]`
// behavior for hooks now living in the lib.
if (isTestMode) {
  setDemoProvider({
    getDemoCampaign: () => fakeTiltifyCampaign as unknown as TiltifyCampaign,
    getDemoUser: () => fakeTiltifyUser as unknown as TiltifyUser,
    getDemoPolls: () => [fakeTiltifyPoll] as unknown as TiltifyPoll[],
  });
}

createRoot(document.getElementById("app")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
